import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Upload, X, Loader2, GripVertical, Star } from "lucide-react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const blank = { title: "", partner: "", badge: "", summary: "", images: [], image_labels: [], steps: [], featured: true, order: 0 };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, {} = new/edit form
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...blank }); setEditing("new"); };
  const startEdit = (p) => { setForm({ ...blank, ...p }); setEditing(p.id); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/admin/projects/upload-image", fd);
        setForm((f) => ({ ...f, images: [...f.images, r.data.path], image_labels: [...f.image_labels, ""] }));
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eroare la încărcare"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const setLabel = (i, v) => setForm((f) => { const l = [...f.image_labels]; l[i] = v; return { ...f, image_labels: l }; });
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i), image_labels: f.image_labels.filter((_, j) => j !== i) }));

  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { title: "", desc: "" }] }));
  const setStep = (i, k, v) => setForm((f) => { const s = [...f.steps]; s[i] = { ...s[i], [k]: v }; return { ...f, steps: s }; });
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Adaugă un titlu");
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0, steps: form.steps.filter((s) => s.title.trim()) };
    delete payload.id;
    try {
      if (editing === "new") await api.post("/admin/projects", payload);
      else await api.put(`/admin/projects/${editing}`, payload);
      toast.success("Proiect salvat");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Ștergi acest proiect?")) return; await api.delete(`/admin/projects/${id}`); toast.success("Proiect șters"); load(); };

  if (editing) {
    return (
      <div data-testid="project-editor">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-lg">{editing === "new" ? "Proiect nou" : "Editează proiect"}</h2>
          <button onClick={() => setEditing(null)} className="text-sm text-slate-500 hover:text-slate-900">← Înapoi</button>
        </div>
        <div className="space-y-4 max-w-2xl">
          <div><Label className="text-xs uppercase font-bold text-slate-500">Titlu *</Label><Input value={form.title} onChange={set("title")} className="rounded-none mt-1.5" data-testid="project-title" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase font-bold text-slate-500">Partener (ex: KATRES × OXO)</Label><Input value={form.partner} onChange={set("partner")} className="rounded-none mt-1.5" data-testid="project-partner" /></div>
            <div><Label className="text-xs uppercase font-bold text-slate-500">Ordine</Label><Input type="number" value={form.order} onChange={set("order")} className="rounded-none mt-1.5" /></div>
          </div>
          <div><Label className="text-xs uppercase font-bold text-slate-500">Badge (ex: Reprezentant oficial...)</Label><Input value={form.badge} onChange={set("badge")} className="rounded-none mt-1.5" data-testid="project-badge" /></div>
          <div><Label className="text-xs uppercase font-bold text-slate-500">Descriere</Label><Textarea value={form.summary} onChange={set("summary")} className="rounded-none mt-1.5 min-h-[110px]" data-testid="project-summary" /></div>

          <div>
            <Label className="text-xs uppercase font-bold text-slate-500">Imagini (prima = principală)</Label>
            <div className="space-y-2 mt-2">
              {form.images.map((img, i) => (
                <div key={i} className="flex items-center gap-2">
                  <img src={mediaUrl(img)} alt="" className="w-14 h-14 object-cover border border-slate-200 shrink-0" />
                  <Input value={form.image_labels[i] || ""} onChange={(e) => setLabel(i, e.target.value)} placeholder="Etichetă imagine" className="rounded-none h-9" />
                  <button onClick={() => removeImage(i)} className="text-slate-400 hover:text-red-500 shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <label className="inline-flex items-center gap-2 border border-dashed border-slate-300 hover:border-orange-500 px-4 py-2 text-sm cursor-pointer transition-colors" data-testid="project-upload-label">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Adaugă imagini
                <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} data-testid="project-upload-input" />
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase font-bold text-slate-500">Etape proiect</Label>
              <button onClick={addStep} className="text-xs font-bold text-orange-600 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Etapă</button>
            </div>
            <div className="space-y-2 mt-2">
              {form.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <GripVertical className="w-4 h-4 text-slate-300 mt-2.5 shrink-0" />
                  <Input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} placeholder="Titlu etapă" className="rounded-none h-9 w-1/3" />
                  <Input value={s.desc} onChange={(e) => setStep(i, "desc", e.target.value)} placeholder="Descriere" className="rounded-none h-9 flex-1" />
                  <button onClick={() => removeStep(i)} className="text-slate-400 hover:text-red-500 mt-2.5 shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} data-testid="project-featured" />
            <span className="text-sm">Proiect principal (afișat mare pe homepage)</span>
          </label>

          <button onClick={save} disabled={saving || uploading} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="save-project">
            {saving ? "Se salvează..." : "Salvează proiectul"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-lg">Proiecte de referință ({projects.length})</h2>
        <button onClick={startNew} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm font-bold uppercase transition-colors" data-testid="new-project"><Plus className="w-4 h-4" /> Proiect nou</button>
      </div>
      <div className="space-y-3">
        {projects.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun proiect.</div>}
        {projects.map((p) => (
          <div key={p.id} className="border border-slate-200 p-4 flex gap-4" data-testid={`admin-project-${p.id}`}>
            {p.images[0] && <img src={mediaUrl(p.images[0])} alt={p.title} className="w-24 h-20 object-cover bg-slate-100 border border-slate-200 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold flex items-center gap-2">{p.title}{p.featured && <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />}</div>
              <div className="text-xs text-slate-500 mt-0.5">{p.partner} · {p.images.length} imagini · {(p.steps || []).length} etape · ordine {p.order}</div>
              <p className="text-sm text-slate-500 mt-1 line-clamp-1">{p.summary}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1 text-xs font-bold uppercase transition-colors" data-testid={`edit-project-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Editează</button>
                <button onClick={() => del(p.id)} className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 px-3 py-1 text-xs font-bold uppercase transition-colors" data-testid={`delete-project-${p.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
